import React, { useState, useEffect, useCallback } from "react";
import { BottomScrollListener } from "react-bottom-scroll-listener";
import I18n from 'i18n.js.erb'
import {
    DEFAULT_PER_PAGE_PARAM,
    MEDIUM_DEBOUNCE_TIME,
    pluralize
} from "../../utils/utils";
import ErrorModal from "./ErrorModal";
import { debounce } from "lodash";
import { Expander } from "./IndexSubmissionLists";
import SaleItemsList from "./SaleItemsList";
import UpdateSaleItemsStateButton from "./UpdateSaleItemsStatesButton";
import SaleItemsChart from "./SaleItemsChart";




const IndexSaleItems = ({
    original_sale_items,
    states,
    sorting_options,
    userId,
    saleMethods,
    saleChannels,
    initial_filters,
    saleChannelsLinks
}) => {

    const originalSaleItems = JSON.parse(original_sale_items)
    const availableSaleChannels = JSON.parse(saleChannels);
    const [initCompleted, setInitCompleted] = useState(false)
    const [saleItems, setSaleItems] = useState([])
    const [saleItemsCount, setSaleItemsCount] = useState(0);
    const [statsToRender, setStatsToRender] = useState(null);
    const [renderStatistics, setRenderStatistics] = useState(false);
    const initialFilters = {
        state: null, sale_channel_id: null, sale_method: null, name: null, serial_number: null, sort_by: null, sorting_direction: 'asc'
    }
    const [filters, setFilters] = useState({ ...initialFilters, ...initial_filters });
    const [filtersEnabled, setFiltersEnabled] = useState(true);
    const [expandAll, setExpandAll] = useState(false);
    const [page, setPage] = useState(1);
    const [hasLoadedAllSaleItems, setHasLoadedAllSaleItems] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [saleItemsAnimationDivider, setSaleItemsAnimationDivider] = useState(Number.MAX_SAFE_INTEGER);


    useEffect(() => {
        if (!initCompleted) {
            //Estraggo dal server le statistiche di vendita dell'utente da renderizzare sui grafici
            let url = `/users/${userId}/sale_items/all/stats.json`

            fetch(url, {
                method: 'GET'
            })
                .then(response => response.json())
                .then((stats) => {
                    setStatsToRender(stats);
                })
                .catch((error) => {
                    console.error("ERROR:", error);
                    setShowErrorModal(true);
                });
        }
    }, [])

    useEffect(() => {
        if (initCompleted && saleItems.length === 0) {
            getSaleItems()
        }
    }, [saleItems]);

    useEffect(() => {
        if (!initCompleted) {
            setInitCompleted(true);
            appendSaleItems(originalSaleItems.sale_items);
            setSaleItemsCount(originalSaleItems.sale_items_count);
        }
    }, []);

    useEffect(() => {
        if (initCompleted) {
            debouncedUpdateFilters();
            return debouncedUpdateFilters.cancel;
        }
    }, [filters.name, filters.serial_number, debouncedUpdateFilters]);


    // Funzione che permette di ottenere dal server nuovi sale items da renderizzare sull'interfaccia grafica
    const getSaleItems = () => {
        let url = `/users/${userId}/sale_items.json?page=${page}&per_page=${DEFAULT_PER_PAGE_PARAM}`
        Object.entries(filters).forEach(([fieldName, fieldValue]) => {
            if (fieldValue !== null)
                url += `&${fieldName}=${fieldValue}`
            else {
                if (fieldName === 'sort_by')
                    url += `&${fieldName}=default`
                if (fieldName === 'sorting_direction')
                    url += `&${fieldName}=asc`
            }
        })

        if (!hasLoadedAllSaleItems) {
            setSaleItemsAnimationDivider(DEFAULT_PER_PAGE_PARAM);
            fetch(url, {
                method: 'GET'
            })
                .then(response => response.json())
                .then((data) => {
                    setLoading(false);
                    if (expandAll) {
                        const newSaleItems = data.sale_items.map(sale_item => {
                            const newSaleItem = { ...sale_item };
                            newSaleItem['display_content'] = expandAll;
                            return newSaleItem;
                        })
                        appendSaleItems(newSaleItems);
                    } else {
                        appendSaleItems(data.sale_items);
                    }
                    setSaleItemsCount(data.sale_items_count)
                })
                .catch((error) => {
                    console.error("ERROR:", error);
                    setShowErrorModal(true);
                });
        }
    }

    // Funzione che permette di concatenare nuovi annunci di vendita ottenuti del server all'array che contiene tutti gli
    // annunci di vendita da mostrare sull'interfaccia grafica all'utente
    const appendSaleItems = (newSaleItems) => {
        setSaleItems(saleItems.concat(newSaleItems));
        setPage(page + 1);
        if (newSaleItems.length < DEFAULT_PER_PAGE_PARAM)
            setHasLoadedAllSaleItems(true)
    }

    // Funzione che permette di ottenere dal server gli annunci dell'utente che matchano con i filtri
    // di ricerca da lui specificati sull'interfaccia grafica
    const getSaleItemsByMatchingFilters = (filter, value) => {
        const newFilters = { ...filters }
        newFilters[filter] = value;
        updateFilters(newFilters)
    }

    // Funzione che aggiorna i filtri di ricerca degli annunci di vendita specificati dall'utente 
    //sull'interfaccia grafica e rilancia la ricerca sugli annunci di vendita che matchano con i nuovi filtri specificati
    const updateFilters = (newFilters) => {
        setFilters(newFilters);
        setLoading(true);
        setPage(1);
        setHasLoadedAllSaleItems(false);
        setSaleItems([]);
    }

    //Funzione che esegue l'update debouncato del filtro di ricerca degli annunci di vendita per nome del prodotto o
    //numero di certificato di gradazione
    const debouncedUpdateFilters = useCallback(
        debounce(() => updateFilters(filters), MEDIUM_DEBOUNCE_TIME),
        [filters.name, filters.serial_number]
    );

    //Funzione che setta il nuovo filtro di ricerca degli annunci di vendita per nome del prodotto o
    //numero di certificato di gradazione
    function onChangeDebouncedFilterHandler(key, value) {
        const newFilters = { ...filters }
        newFilters[key] = value;
        setFilters(newFilters)
    }

    // Funzione che aggiorna tutti gli annunci di vendita renderizzati sull'interfaccia grafica in corrispondenza
    // di un determinato parametro
    const handleChangeAllSaleItems = (key, value) => {
        let newSaleItems = saleItems.map((s) => {
            const newSaleItem = { ...s };
            newSaleItem[key] = value;
            return newSaleItem;
        });
        setSaleItems(newSaleItems)
    }

    // Funzione che gestisce l'espansione / compattamento di tutti gli annunci di vendita renderizzati
    // sull'interfaccia grafica
    const handleExpandAll = () => {
        setExpandAll(!expandAll);
        handleChangeAllSaleItems('display_content', !expandAll);
    }



    return (
        <React.Fragment>
            {/* SEZIONE DEDICATA ALL'HEADER DEGLI ANNUNCI DI VENDITA */}
            <div>
                {/* SEZIONE DEDICATA AI GRAFICI CHE RIPORTANO LE STATISTICHE DI VENDITA DELL'UTENTE */}
                {statsToRender &&
                    <div className="row d-flex justify-content-center flex-wrap m-auto">
                        {statsToRender.map((stats, index) => {
                            return <div className={`col-12 col-md-6 col-xxl-4 d-flex w-100 m-0 py-3 px-${![0, statsToRender.length - 1].includes(index) ? '2' : '0'}`} key={index}>
                                <SaleItemsChart
                                    stats={stats}
                                    renderStatistics={renderStatistics}
                                    setRenderStatistics={setRenderStatistics}
                                />
                            </div>
                        })}
                    </div>
                }



                {/*  SEZIONE DEDICATA AI FILTRI DELLA PAGINA */}
                <div className="row mx-0 br-8px border-aiG-light-gray-imp bg-aiG-gray3-imp">
                    <div className="col-12">
                        <div className="row d-flex justify-content-between px-3 py-2">
                            <div className="w-50">
                                <div className="d-flex align-items-center justify-content-start text-left cursor-pointer"
                                    onClick={(e) => setFiltersEnabled(!filtersEnabled)}>
                                    <b>{I18n.t('js.filters.filters')}</b>
                                    <img src={`/images/icons/black/arrow-${filtersEnabled ? 'up' : 'down'}.svg`} className="cursor-pointer ml-2"
                                        alt="" width="10" height="10"
                                    />
                                </div>
                            </div>

                            <div className="w-50">
                                <div className="d-flex align-items-center justify-content-end blue-label text-right cursor-pointer"
                                    onClick={() => updateFilters(initialFilters)}>
                                    <img src="/images/icons/blue/refresh.svg" className="mr-2" id="reset"
                                        alt="" width="15" height="15"
                                    />
                                    {I18n.t('js.filters.cancelFilters')}
                                </div>
                            </div>
                        </div>

                        <div className={`row slide-animation-container slide-${filtersEnabled ? 'down' : 'up'}`}>
                            <hr className="separator my-0 w-100" />

                            <div className="col-12 pr-0">
                                <div className="row w-100 py-2">
                                    <div className="col-6 col-md-3 col-lg-2 mb-2 py-1">
                                        <p className="mb-0 filter-label">{I18n.t('js.indexSaleItems.filters.state')}</p>
                                        <select
                                            className={`form-control filter-input ${filters.state ? 'selected-label' : 'border-none'} pl-2`}
                                            value={filters.state ? filters.state : ""}
                                            name="state_filter"
                                            id="state_filter"
                                            onChange={(e) => getSaleItemsByMatchingFilters('state', e.target.value)}
                                        >
                                            <option value={""}></option>
                                            {states.map(state => {
                                                return <option value={state} key={state}>
                                                    {I18n.t(`js.indexSaleItems.states.${state}`)}
                                                </option>
                                            })}
                                        </select>
                                    </div>

                                    {availableSaleChannels.length > 1 &&
                                        <div className="col-6 col-md-3 col-lg-2 mb-2 py-1">
                                            <p className="mb-0 filter-label">{I18n.t('js.indexSaleItems.sort.saleChannel')}</p>
                                            <select
                                                className={`form-control filter-input ${filters.sale_channel_id ? 'selected-label' : 'border-none'} pl-2`}
                                                value={filters.sale_channel_id ? filters.sale_channel_id : ""}
                                                name="sale_channel_filter"
                                                id="sale_channel_filter"
                                                onChange={(e) => getSaleItemsByMatchingFilters('sale_channel_id', e.target.value)}
                                            >
                                                <option value={""}></option>
                                                {availableSaleChannels.map(saleChannel => {
                                                    return <option value={saleChannel.id} key={saleChannel.id}>
                                                        {saleChannel.meta_name}
                                                    </option>
                                                })}
                                            </select>
                                        </div>
                                    }

                                    {saleMethods.length > 1 &&
                                      <div className="col-6 col-md-3 col-lg-2 mb-2 py-1">
                                          <p className="mb-0 filter-label">{I18n.t('js.indexSaleItems.filters.saleMethod')}</p>
                                          <select
                                            className={`form-control filter-input ${filters.sale_method ? 'selected-label' : 'border-none'} pl-2`}
                                            value={filters.sale_method ? filters.sale_method : ""}
                                            name="sale_method_filter"
                                            id="sale_method_filter"
                                            onChange={(e) => getSaleItemsByMatchingFilters('sale_method', e.target.value)}
                                          >
                                              <option value={""}></option>
                                              {saleMethods.map(sale_method => {
                                                  return <option value={sale_method} key={sale_method}>
                                                      {I18n.t(`js.indexSaleItems.saleMethods.${sale_method}`)}
                                                  </option>
                                              })}
                                          </select>
                                      </div>
                                    }

                                    <div className="col-6 col-md-3 col-lg-2 mb-2 py-1">
                                        <p className="mb-0 filter-label">{I18n.t('js.indexSaleItems.filters.productName')}</p>
                                        <input
                                            type="text"
                                            value={filters.name ? filters.name : ""}
                                            className={`form-control filter-input ${filters.name ? 'selected-label' : 'border-none'}`}
                                            onChange={(e) => onChangeDebouncedFilterHandler('name', e.target.value)}
                                            name="name_filter"
                                            id="name_filter"
                                        />
                                    </div>

                                    <div className="col-6 col-md-3 col-lg-2 mb-2 py-1">
                                        <p className="mb-0 filter-label">{I18n.t('js.indexSaleItems.filters.certificateNumber')}</p>
                                        <input
                                            type="text"
                                            value={filters.serial_number ? filters.serial_number : ""}
                                            className={`form-control filter-input ${filters.serial_number ? 'selected-label' : 'border-none'}`}
                                            onChange={(e) => onChangeDebouncedFilterHandler('serial_number', e.target.value)}
                                            name="serial_number_filter"
                                            id="serial_number_filter"
                                        />
                                    </div>

                                    <div className="col-6 col-md-3 col-lg-3 mb-2 py-1">
                                        <p className="mb-0 filter-label">{I18n.t('js.indexSubmissionListsPage.sortBy')}</p>
                                        <select
                                            className={`form-control filter-input ${filters.sort_by ? 'selected-label' : 'border-none'} pl-2`}
                                            name="sort_by"
                                            id="sort_by"
                                            value={filters.sort_by ? filters.sort_by : ''}
                                            onChange={(e) => getSaleItemsByMatchingFilters('sort_by', e.target.value)}>
                                            {sorting_options.filter(option => option.type === 'sort').map(option => {
                                                return <option value={option.value} key={option.value}>{I18n.t(option.locale)}</option>
                                            })}
                                            <option disabled>___________________</option>
                                            {sorting_options.filter(option => option.type === 'group_by').map(option => {
                                                return <option value={option.value} key={option.value}>{I18n.t(option.locale)}</option>
                                            })}
                                        </select>
                                    </div>

                                    {
                                        filters.sort_by &&
                                        <div className="col-6 col-md-3 col-lg-1 sd-flex align-content-end align-self-end mb-2 py-1">
                                            <div className="d-flex justify-content-center align-items-center px-0 cursor-pointer ml-3"
                                                onClick={(e) => getSaleItemsByMatchingFilters('sorting_direction', filters.sorting_direction === 'desc' ? 'asc' : 'desc')}>
                                                <div className="d-flex justify-content-center align-items-center blue-label minW-38px">
                                                    <p className="my-0 mr-2">
                                                        {filters.sorting_direction === 'desc' ? '↓' : '↑'}
                                                    </p>
                                                    <p className="my-0">
                                                        {I18n.t(`js.filters.sort.${filters.sorting_direction}endingSuperCompact`)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>





                {/*  SEZIONE DEDICATA ALLA GESTIONE DEI RISULTATI RENDERIZZATI SULLA PAGINA */}
                <div
                    className="mt-4 mb-3 d-block d-sm-flex justify-content-start justify-content-sm-between align-items-center border-bottom-2px-lightgray3">
                    <div className="d-flex justify-content-between align-items-center pb-3 px-0">
                        <div className="align-items-center">
                            <b>{saleItemsCount}</b>&nbsp;
                            {pluralize(saleItemsCount, 'indexSaleItems.saleItem').toLowerCase()}&nbsp;
                        </div>

                        {

                            initCompleted && saleItemsCount > 0 &&
                            <div className="d-flex d-sm-none align-items-center">
                                <Expander
                                    expandAll={expandAll}
                                    handleExpandAll={handleExpandAll}
                                />
                            </div>
                        }
                    </div>

                    {
                        initCompleted && saleItemsCount > 0 &&
                        <div className="d-flex justify-content-start justify-content-sm-end pb-3 px-0">
                            <div className="d-none d-sm-flex align-items-center">
                                <Expander
                                    expandAll={expandAll}
                                    handleExpandAll={handleExpandAll}
                                />
                            </div>

                            <UpdateSaleItemsStateButton
                                userId={userId}
                                reloadPageUrl={`/users/${userId}/sale_items`}
                                setShowErrorModal={setShowErrorModal}
                            />
                        </div>
                    }
                </div>
            </div>



            {/*  SEZIONE DEDICATA ALL'ELENCO DI TUTTI GLI ANNUNCI DI VENDITA */}
            <div>
                {
                    loading ?
                        <div className="d-flex justify-content-center my-5">
                            <div className="d-flex align-items-center text-intense-blue text-weight-500">
                                <div className="spinner-border" role="status" />
                                <b className="ml-2">{I18n.t('js.indexSaleItems.loading')}</b>
                            </div>
                        </div>
                        :
                        saleItems.length > 0 ?
                            <BottomScrollListener onBottom={getSaleItems}>
                                <SaleItemsList
                                    saleItems={saleItems}
                                    setSaleItems={setSaleItems}
                                    saleItemsAnimationDivider={saleItemsAnimationDivider}
                                    saleItemsCount={saleItemsCount}
                                    setSaleItemsCount={setSaleItemsCount}
                                    saleChannelsLinks={saleChannelsLinks}
                                />
                                {
                                    !hasLoadedAllSaleItems && saleItems.length > 0 &&
                                    <div className="d-flex justify-content-center mb-5  mt-0">
                                        <div className="d-flex align-items-center text-intense-blue text-weight-500">
                                            <div className="spinner-border" role="status" />
                                            <b className="ml-2">{I18n.t('js.indexSaleItems.loading')}</b>
                                        </div>
                                    </div>
                                }
                            </BottomScrollListener>
                            :
                            <div className="d-flex justify-content-center">
                                <div className="d-flex align-items-center text-muted ml-md-3 mr-md-3 mt-md-4 text-center text-space">
                                    <b className="ml-2">{I18n.t('js.indexSaleItems.noResultsFoundSaleItems')}</b>
                                </div>
                            </div>
                }
            </div>

            <ErrorModal
                showErrorModal={showErrorModal}
                setShowErrorModal={setShowErrorModal}
            />
        </React.Fragment>
    )
}

export default IndexSaleItems;